'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type UnitSystem = 'imperial' | 'metric';

interface UnitContextType {
    unitSystem: UnitSystem;
    setUnitSystem: (unit: UnitSystem) => void;
    // Helper to get unit label
    unitLabel: string;
    // Conversion factor from OpenSCAD (mm) to display units
    conversionFactor: number;
}

const UnitContext = createContext<UnitContextType | undefined>(undefined);

const UNIT_STORAGE_KEY = 'daddycad_unit_system';

export function UnitProvider({ children }: { children: ReactNode }) {
    const [unitSystem, setUnitSystemState] = useState<UnitSystem>('imperial');

    // Load from localStorage on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(UNIT_STORAGE_KEY);
            if (saved === 'metric' || saved === 'imperial') {
                setUnitSystemState(saved);
            }
        }
    }, []);

    const setUnitSystem = (unit: UnitSystem) => {
        setUnitSystemState(unit);
        if (typeof window !== 'undefined') {
            localStorage.setItem(UNIT_STORAGE_KEY, unit);
        }
    };

    const unitLabel = unitSystem === 'imperial' ? 'in' : 'mm';
    // OpenSCAD uses mm by default; for imperial we need to scale by 1/25.4
    const conversionFactor = unitSystem === 'imperial' ? 1 / 25.4 : 1;

    return (
        <UnitContext.Provider value={{ unitSystem, setUnitSystem, unitLabel, conversionFactor }}>
            {children}
        </UnitContext.Provider>
    );
}

export function useUnit() {
    const context = useContext(UnitContext);
    if (!context) {
        throw new Error('useUnit must be used within a UnitProvider');
    }
    return context;
}
